import React from "react";
import _ from "lodash";
import clsx from "clsx";

import {Checkbox, Radio, CircularProgress, Grid, makeStyles} from "@material-ui/core";

import {DEFAULT_EMPTY_SECTION_MESSAGE} from "../../../../utils/constants";
import BaseDialog from "../../../../components/BaseDialog/BaseDialog";
import {getTableStructureSwitch} from "../../../../components/ChartsV2/InstrumentsAllocationTable/table-data";
import ChartSectionBordered, {ChartSectionPortfolioName} from "../ChartSectionBordered/ChartSectionBordered";
import {DepotSummaryNavigation} from "../../components/Widgets/components/InstrumentList/components/InstrumentListItem";
import DashboardTable from "../../../../components/DashboardTable/DashboardTable";

import useStyles from "./styles";
import {useSelectedItems} from "../../../../hooks/useSelectedItems";
import {DisabledReasonTooltip} from "../../../../components/Tooltip";


const useDepotSelectionInstrumentTableClasses = makeStyles((theme) => ({
  tableHead: {
    backgroundColor: '#f9f9f9',

    '& td': {
      color: '#80858C !important'
    }

  },
  tableCell: {
    [theme.breakpoints.down('md')]: {
      width: 75
    },
    '&:first-child': {
      width: '350px !important',
      paddingLeft: 16
    },
    '&:last-child': {
      paddingRight: 10
    },
    '&.sustainability-cell': {
      width: 83,
      minWidth: 83
    }
  },
}));

const SelectableDepotAndInstrumentsTable = ({depot, ContainerProps, InstrumentsProps, expanded, onExpanded, onlyDepot}) => {

  const classes = useStyles();

  const instruments = (depot.components || []).filter((component) => !!component.isin).map((component) => ({
    ...component,
    uniq_id: `${component.isin}_${depot.id}` // Create uniq identifier so we could handle case when asset exists in many depots
  }));

  const onInstrumentSelect = React.useMemo(() => {
    return _.isFunction(InstrumentsProps.handleSelect) ? (instrument) => InstrumentsProps.handleSelect(instrument, depot) : undefined;
  }, [InstrumentsProps.handleSelect]);

  const onAllSelect = React.useMemo(() => {
    return _.isFunction(InstrumentsProps.handleSelectAll) ? () => InstrumentsProps.handleSelectAll(depot, instruments) : undefined;
  }, [InstrumentsProps.handleSelectAll]);

  const CheckComponent = ContainerProps.singleSelection ? Radio : Checkbox;

  return (
    <ChartSectionBordered
      dataId={`comparison-table-${depot.id}`}
      title={(
        <DisabledReasonTooltip title={depot.disabled && depot.disabledReason}>
          <span style={{display: 'flex'}}>
            <CheckComponent
              color="primary"
              className={clsx(classes.containerCheckbox, _.get(ContainerProps, 'classes.containerCheckbox'))}
              checked={ContainerProps.checked}
              onChange={() => ContainerProps.onChange(depot)}
              disabled={depot.disabled}
              disableRipple
            />
            <ChartSectionPortfolioName portfolio={depot} />
          </span>
        </DisabledReasonTooltip>
      )}
      titleControl={onlyDepot ? undefined : (<DepotSummaryNavigation depot={depot} />)}
      showTitleControlFolded
      expanded={expanded}
      isPortfolioSection
      skipContentPadding
      borderLeftColor={depot.color}
      onExpanded={onlyDepot ? undefined : onExpanded}
      headerDisabled={depot.disabled}
      content={onlyDepot ? undefined : (
        <div style={{overflowX: 'auto', overflowY: "hidden"}}>
          <DashboardTable
            tableLayout={"auto"}
            structure={InstrumentsProps.tableStructure}
            dataSource={instruments || []}
            expanded={true}
            tableClasses={InstrumentsProps.tableClasses}
            withFooter={false}
            options={{
              onAllSelect: onAllSelect,
              totalInstrumentsCount: InstrumentsProps.totalInstrumentsCount || (instruments && instruments.length),
              onInstrumentSelect: onInstrumentSelect,
              selected: InstrumentsProps.selected,
              ...(InstrumentsProps.tableOptions || {})
            }}
          />
        </div>
      )}
    />
  )
};

SelectableDepotAndInstrumentsTable.defaultProps = {
  InstrumentsProps: {},
  ContainerProps: {}
};

const DepotSelectionModal = (props) => {
  const {
    open,
    onClose,
    title,
    subHeader,
    instrumentsData,
    depots,
    selectedItems,
    handleContainerCheckboxChange,
    handleSelectAllInstruments,
    handleInstrumentSelect,
    selectedInstruments,
    actions,
    singleSelection,
    onlyDepots,
  } = props;

  const classes = useStyles();
  const tableClasses = useDepotSelectionInstrumentTableClasses();
  const tableStructure = getTableStructureSwitch();

  const expandedItems = useSelectedItems('id');

  React.useEffect(() => {
    if (_.isEmpty(depots)) return;

    expandedItems.cleanSelected();
    if (depots.length == 1) {
      //If there is only one depot - expand it by default
      expandedItems.handleToggleItem({id: _.get(depots, '0.id')});
    }

  }, [depots]);

  return(
    <BaseDialog
      open={open}
      handleClose={onClose}
      title={title}
      actions={
        <>
          {!instrumentsData.loading && (
            <Grid container spacing={2} alignItems={"center"} justify={"flex-end"}>
              <Grid xs={12} sm={true} item>
                <Grid container justify="flex-end" spacing={2}>
                  {actions}
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      }
    >
      {instrumentsData.loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <Grid container spacing={2}>
          {depots.length > 0 ? (
            <>
              <Grid item xs={12}>
                <span className={classes.subHeader}>{subHeader}</span>
              </Grid>
              {depots.map((depot) => (
                <Grid key={depot.id} item xs={12}>
                  <SelectableDepotAndInstrumentsTable
                    depot={depot}
                    onlyDepot={onlyDepots}
                    expanded={depot.id in expandedItems.selectedItems}
                    onExpanded={() => expandedItems.handleToggleItem({id: depot.id})}
                    ContainerProps={{
                      singleSelection: singleSelection,
                      checked: depot.id in selectedItems,
                      onChange: handleContainerCheckboxChange
                    }}
                    InstrumentsProps={{
                      tableStructure,
                      tableClasses,
                      handleSelectAll: handleSelectAllInstruments,
                      handleSelect: handleInstrumentSelect,
                      selected: (selectedInstruments || []).filter((product) => product.depotId == depot.id).map((product) => product.data),
                    }}
                  />
                </Grid>
              ))}
            </>
          ): (
            <Grid item xs={12} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <p>{DEFAULT_EMPTY_SECTION_MESSAGE}</p>
            </Grid>
          )}
        </Grid>
      )}
    </BaseDialog>
  )
};

DepotSelectionModal.defaultProps = {
  singleSelection: false,
};

export default DepotSelectionModal;